/**
 * 2-Level Permission System
 *
 * Level 1: Role-based permissions (user's role determines base access)
 * Level 2: Folder security (isFolderSecurityEdit, securities[], securityTeams[])
 *
 * See: docs/subsystems/ted/fundamentals/folder-security.md
 */
import { useAuthStore } from '@/stores/auth-store';
import { useTeamStore } from '@/stores/team-store';
import {
  ROLE_TYPES,
  type RoleType,
  type MeResponse,
  type BaseFolderSecurity,
  type CompanyInfo,
} from '@scio3/shared-types';

// Root-level admin roles (for isRootAdmin — rootRole is never TEAM_OWNER)
const ROOT_ADMIN_ROLES: RoleType[] = [ROLE_TYPES.ADMIN, ROLE_TYPES.SUPER_ADMIN, ROLE_TYPES.OWNER];

// Team-level admin roles (includes TEAM_OWNER for isTeamAdmin/isAdminOnTeam)
const ADMIN_ROLES: RoleType[] = [
  ROLE_TYPES.ADMIN,
  ROLE_TYPES.SUPER_ADMIN,
  ROLE_TYPES.OWNER,
  ROLE_TYPES.TEAM_OWNER,
];

// Standard roles that can have security fully disabled
const STANDARD_ROLES: RoleType[] = [
  ROLE_TYPES.OWNER,
  ROLE_TYPES.SUPER_ADMIN,
  ROLE_TYPES.ADMIN,
  ROLE_TYPES.USER,
];

type Entity = 'screen' | 'asset' | 'playlist' | 'schedule';

interface FolderContext {
  folder?: BaseFolderSecurity | null;
  /** For items inside folders, we need the parent folder's security settings */
  parentFolder?: BaseFolderSecurity | null;
}

interface PermissionOptions {
  /**
   * Account payment has lapsed (`authStore.isAccountInactive`). Forces every write
   * permission to false so the UI stops offering actions the backend will refuse:
   * `API_IGNORE_INACTIVE_ACCOUNT` in open-api's `get-auth-context.decorator.ts` allows a
   * lapsed account to READ its content but rejects every content mutation. Without this the
   * paywalled portal would show Add Screen, New Folder and row menus that all fail.
   *
   * Role and admin flags are deliberately left untouched — the trimmed settings nav still
   * gates on `isRootAdmin` to decide whether Subscription Plan and Payment Settings show.
   */
  isAccountInactive?: boolean;
}

// -----------------------------------------------------------------------------
// Level 1: Role-based permissions
// -----------------------------------------------------------------------------

/**
 * Level 1: Role-based permissions (pure function, testable without React).
 * Evaluates RBAC rules based on user's root role and active team role.
 */
export function getRolePermissions(me: MeResponse | null, activeTeamId: string) {
  const currentTeam = me?.teams.find((t) => t._id === activeTeamId);
  const rootRole = me?.rootRole;
  const teamRole = currentTeam?.role;

  const isRootOwner = rootRole === ROLE_TYPES.OWNER;
  const isRootAdmin = ROOT_ADMIN_ROLES.includes(rootRole as RoleType);
  const isSuperAdmin = rootRole === ROLE_TYPES.SUPER_ADMIN;
  const isRootSuperAdmin = isRootOwner || isSuperAdmin;
  const isTeamAdmin = ADMIN_ROLES.includes(teamRole as RoleType);

  function canCreate(entity: Entity): boolean {
    // Auth not loaded yet — deny until we know the user's role
    if (!teamRole) return false;
    if (isTeamAdmin) return true;

    const perms = currentTeam?.permissions;
    // Custom role (ID > 1 char) without permissions = deleted role, deny all actions
    if (!perms && teamRole.length > 1) return false;
    if (perms) {
      return perms[`${entity}CanCreate`] ?? false;
    }

    switch (teamRole) {
      case ROLE_TYPES.CONTENT_PROPOSAL:
        return entity === 'asset';
      case ROLE_TYPES.CONTENT_APPROVAL:
        return true;
      case ROLE_TYPES.READ_ONLY:
        return false;
      default:
        return true;
    }
  }

  function canEdit(entity: Entity): boolean {
    // Auth not loaded yet — deny until we know the user's role
    if (!teamRole) return false;
    if (isTeamAdmin) return true;

    const perms = currentTeam?.permissions;
    // Custom role (ID > 1 char) without permissions = deleted role, deny all actions
    if (!perms && teamRole.length > 1) return false;
    if (perms) {
      return perms[`${entity}CanEdit`] ?? false;
    }

    switch (teamRole) {
      case ROLE_TYPES.CONTENT_PROPOSAL:
        return false;
      case ROLE_TYPES.CONTENT_APPROVAL:
        return true;
      case ROLE_TYPES.READ_ONLY:
        return false;
      default:
        return true;
    }
  }

  function canDelete(entity: Entity): boolean {
    // Auth not loaded yet — deny until we know the user's role
    if (!teamRole) return false;
    if (isTeamAdmin) return true;

    const perms = currentTeam?.permissions;
    // Custom role (ID > 1 char) without permissions = deleted role, deny all actions
    if (!perms && teamRole.length > 1) return false;
    if (perms) {
      return perms[`${entity}CanDelete`] ?? false;
    }

    switch (teamRole) {
      case ROLE_TYPES.CONTENT_PROPOSAL:
        return false;
      case ROLE_TYPES.CONTENT_APPROVAL:
        return true;
      case ROLE_TYPES.READ_ONLY:
        return false;
      default:
        return true;
    }
  }

  return {
    rootRole,
    teamRole,
    isRootOwner,
    isRootAdmin,
    isSuperAdmin,
    isRootSuperAdmin,
    isTeamAdmin,
    canCreate,
    canEdit,
    canDelete,
    currentTeam,
  };
}

// -----------------------------------------------------------------------------
// Level 2: Folder-level security
// -----------------------------------------------------------------------------

/**
 * Level 1 + Level 2: Combined permission check (pure function, testable without React).
 * Combines role-based permissions with folder-level security checks.
 */
export function getPermissions(
  me: MeResponse | null,
  company: CompanyInfo | null,
  activeTeamId: string,
  context: FolderContext = {},
  options: PermissionOptions = {},
) {
  const basePermissions = getRolePermissions(me, activeTeamId);
  const isLapsed = options.isAccountInactive === true;
  const { teamRole } = basePermissions;

  const folder = context.folder ?? context.parentFolder;
  const userId = me?._id;

  // Feature flags from company
  const roleSecurity = company?.feature?.roleSecurity ?? true;
  const folderLevelSecurity = company?.feature?.folderLevelSecurity ?? false;

  // Check if user is root super admin (bypass all)
  const isRootSuperAdmin = basePermissions.isRootSuperAdmin;

  // Check if user is admin on current team (bypass folder checks on their team)
  const isAdminOnTeam = ADMIN_ROLES.includes(teamRole as RoleType);

  // Check if this is definitively a SAME-team folder.
  // Only true when we KNOW the folder's teamId matches activeTeamId.
  // If teamId is undefined/null (e.g., cache miss or race condition), we treat it
  // as NOT same-team to be conservative and avoid granting unintended access.
  const isSameTeamFolder = folder?.teamId != null && folder.teamId === activeTeamId;

  // Admin of the team that OWNS the folder — the active team, or another team the user is
  // also an admin of (scio3#2550: an admin may write into another team's folder only as an
  // admin of that team, or through an Edit share; otherwise the cross-team rules below apply).
  const isAdminOnFolderTeam =
    (isAdminOnTeam && isSameTeamFolder) || isAdminOnTeamId(me, folder?.teamId);

  // Legacy: kept for backwards compatibility in other parts of the return value
  const isCrossTeamFolder = folder?.teamId != null && folder.teamId !== activeTeamId;

  // Check if user has a standard role (can have security disabled)
  const hasStandardRole = STANDARD_ROLES.includes(teamRole as RoleType);

  // Check if roleSecurity is effectively enabled for this user
  const effectiveRoleSecurity = roleSecurity || !hasStandardRole;

  // Check if folderLevelSecurity is effectively enabled for this user
  const effectiveFolderSecurity = folderLevelSecurity || !hasStandardRole;

  /**
   * Check if folder security allows access.
   * Returns true if folder security passes or is bypassed.
   */
  function checkFolderAccess(): boolean {
    // Root Super Admin bypasses all folder checks
    if (isRootSuperAdmin) return true;

    // If no folder context, use role permissions only
    if (!folder) {
      return true;
    }

    // System folders (OnHold) — admins bypass folder checks, non-admins denied
    if (folder.isHoldDevice) return isAdminOnTeam || isRootSuperAdmin;

    // Admin of the folder's OWN team bypasses ALL folder security checks.
    // Team admins have full control over folders in their own team, including
    // read-only (isFolderSecurityEdit=false) folders. This is intentional because
    // team admins are the highest authority on their team - if they can't edit,
    // nobody can, which would lock out the folder permanently.
    // An admin of a DIFFERENT team (e.g. the active one) does NOT get this bypass — they
    // respect folder settings, so a View-only share stays read-only for them.
    // IMPORTANT: Only bypass when we KNOW the folder's team. If folder.teamId is undefined,
    // we conservatively deny the bypass.
    if (isAdminOnFolderTeam) {
      return true;
    }

    // isFolderSecurityEdit controls whether users can EDIT items in this folder.
    // true (default) = users with access can edit; false = view-only.
    //
    // This check happens AFTER the same-team admin bypass above.
    // For cross-team access or non-admin users, respect the read-only flag.
    //
    // For folder operations, we need to check TWO levels:
    // 1. parentFolder.isFolderSecurityEdit - can user edit items INSIDE the parent folder?
    // 2. folder.isFolderSecurityEdit - can THIS specific folder be edited?
    //
    // Both must pass for edit operations on folders.
    // For asset operations, only parentFolder matters (assets don't have isFolderSecurityEdit).
    //
    // IMPORTANT: This setting applies to IMMEDIATE children only.
    // A parent folder's isFolderSecurityEdit does NOT cascade to grandchildren.
    // Example: If FolderA has Edit=OFF and contains FolderB with Edit=ON,
    // users can edit items inside FolderB (FolderB's setting applies).

    // Check parent folder's security (controls what you can do to items inside it)
    if (context.parentFolder?.isFolderSecurityEdit === false) {
      return false;
    }
    // Also check the folder's own security (for folder operations, the folder itself may be read-only)
    // This only applies when context.folder is provided and has isFolderSecurityEdit set
    if (context.folder?.isFolderSecurityEdit === false) {
      return false;
    }

    // If roleSecurity is OFF and user has standard role, skip folder checks
    if (!effectiveRoleSecurity) return true;

    // If folderLevelSecurity is OFF and user has standard role, skip folder checks
    if (!effectiveFolderSecurity) return true;

    // For cross-team folders, check securityTeams[]
    if (isCrossTeamFolder) {
      return checkSecurityTeams(folder, activeTeamId);
    }

    // For same-team folders, check securities[]
    return checkSecurities(folder, userId, isAdminOnTeam);
  }

  const hasFolderAccess = checkFolderAccess();

  // Detect if folder context was provided
  const hasFolderContext = !!(context.folder || context.parentFolder);

  /**
   * Check folder-level permission for edit action.
   */
  function canEditInFolder(entity: Entity): boolean {
    return basePermissions.canEdit(entity) && hasFolderAccess;
  }

  /**
   * Check folder-level permission for delete action.
   */
  function canDeleteInFolder(entity: Entity): boolean {
    return basePermissions.canDelete(entity) && hasFolderAccess;
  }

  /**
   * Check folder-level permission for create action.
   */
  function canCreateInFolder(entity: Entity): boolean {
    return basePermissions.canCreate(entity) && hasFolderAccess;
  }

  // Auto-select folder-aware when context exists, otherwise use role-based.
  // A lapsed account is read-only regardless of role or folder security — see
  // PermissionOptions.isAccountInactive.
  const canCreate = (entity: Entity) =>
    !isLapsed && (hasFolderContext ? canCreateInFolder(entity) : basePermissions.canCreate(entity));
  const canEdit = (entity: Entity) =>
    !isLapsed && (hasFolderContext ? canEditInFolder(entity) : basePermissions.canEdit(entity));
  const canDelete = (entity: Entity) =>
    !isLapsed && (hasFolderContext ? canDeleteInFolder(entity) : basePermissions.canDelete(entity));

  return {
    ...basePermissions,
    canCreate,
    canEdit,
    canDelete,
    hasFolderAccess,
    hasFolderContext,
    /** True when writes are blocked purely because the account's payment lapsed. */
    isReadOnlyLapsedAccount: isLapsed,
    folderLevelSecurity,
    roleSecurity,
    effectiveFolderSecurity,
    effectiveRoleSecurity,
    isCrossTeamFolder: !!isCrossTeamFolder,
  };
}

/**
 * True when the user holds an admin role (Admin / Super Admin / Owner / Team Owner) on
 * `teamId` — read from `me.teams`, so it covers teams other than the active one. An
 * unknown teamId is never an admin match (conservative, like isSameTeamFolder). Callers
 * holding a LOADED folder pass its owner via `folderOwnerTeamId` (`@/lib/folder-security`),
 * so a legacy null-teamId Default-team folder resolves to the root team '1'.
 */
export function isAdminOnTeamId(me: MeResponse | null, teamId: string | null | undefined): boolean {
  if (teamId == null) return false;
  const role = me?.teams.find((t) => t._id === teamId)?.role;
  return ADMIN_ROLES.includes(role as RoleType);
}

/**
 * Check if user is in the securities[] array for same-team folder access.
 */
function checkSecurities(
  folder: BaseFolderSecurity,
  userId: string | undefined,
  isAdmin: boolean,
): boolean {
  const securities = folder.securities;

  // Empty array or null = EVERYONE_TEAM (all team members can access)
  if (!securities || securities.length === 0) {
    return true;
  }

  // Check special userId values
  for (const entry of securities) {
    // "-2" = EVERYONE_ACCOUNT (all users in account)
    if (entry.userId === '-2') return true;

    // "0" = EVERYONE_TEAM (all team members)
    if (entry.userId === '0') return true;

    // "-1" = ONLY_ADMIN baseline. Admins always pass. A non-admin must NOT be rejected
    // here — they may still be listed explicitly elsewhere in the array (#1452: "Admin Only"
    // coexists with individual users, who bypass the admin-only gate). So don't short-circuit
    // on the sentinel; keep scanning for an explicit userId match.
    if (entry.userId === '-1') {
      if (isAdmin) return true;
      continue;
    }

    // Check specific user ID
    if (entry.userId === userId) return true;
  }

  return false;
}

/**
 * Check if current team is in securityTeams[] for cross-team folder access.
 */
function checkSecurityTeams(folder: BaseFolderSecurity, teamId: string): boolean {
  const securityTeams = folder.securityTeams;

  // If no securityTeams, folder is not shared cross-team
  if (!securityTeams || securityTeams.length === 0) {
    return false;
  }

  // Check if current team is in the list
  if (securityTeams.includes(teamId)) {
    return true;
  }

  // Check for "-2" (EVERYONE_ACCOUNT - all teams in account)
  if (securityTeams.includes('-2')) {
    return true;
  }

  return false;
}

// -----------------------------------------------------------------------------
// React Hooks
// -----------------------------------------------------------------------------

/**
 * React hook for combined permissions (Level 1 + Level 2).
 *
 * When folder context is provided, canEdit/canCreate/canDelete auto-select folder-aware checks.
 * When no folder context, they use role-based checks only.
 *
 * @example
 * // Without folder context - uses role-based permissions
 * const { canEdit } = usePermissions();
 * canEdit('screen'); // Role-based only
 *
 * @example
 * // With folder context - auto-selects folder-aware permissions
 * const { canEdit } = usePermissions({ parentFolder });
 * canEdit('screen'); // Role + folder security combined
 *
 * @param context - Optional folder context for folder-level security checks
 */
export function usePermissions(context: FolderContext = {}) {
  const me = useAuthStore((s) => s.me);
  const company = useAuthStore((s) => s.company);
  const activeTeamId = useTeamStore((s) => s.activeTeamId);
  const isAccountInactive = useAuthStore((s) => s.isAccountInactive);
  return getPermissions(me, company, activeTeamId, context, { isAccountInactive });
}

/**
 * @deprecated Use usePermissions({ folder }) or usePermissions({ parentFolder }) instead.
 * Alias for usePermissions with folder context.
 */
export function useFolderPermissions(context: FolderContext = {}) {
  return usePermissions(context);
}
